<template>
  <div class="relative flex-shrink-0 rounded">
    <div
      class="flex flex-col w-full h-full"
      :class="{ 'pointer-events-none': readonly }"
    >
      <div
        class="h-1.5 w-full flex-shrink-0 rounded-t"
        :style="{
          backgroundColor:
            measurement.target && measurement.target.curriculum_color
        }"
      ></div>
      <div
        v-if="!isCollapsed"
        id="measurememt-header"
        class="flex items-center justify-between flex-shrink-0 w-full px-4 h-9 bg-prim-200"
      >
        <div
          class="flex items-center justify-center w-6 h-6 transition-all duration-300 rounded cursor-pointer hover:bg-white"
          :class="{ 'bg-pure-white': display === 'description' }"
          @click="
            display = display === 'description' ? 'target' : 'description'
          "
        >
          <BlIcon icon="article" class="w-6 h-6" />
        </div>
        <div
          v-if="sessionStatus !== 'draft'"
          class="relative flex items-center justify-center w-6 h-6 transition-all duration-300 rounded cursor-pointer hover:bg-white"
          :class="{ 'bg-pure-white': display === 'comment' }"
          @click="display = display === 'comment' ? 'target' : 'comment'"
        >
          <BlIcon icon="chat" class="w-6 h-6" />
          <div
            class="absolute w-2 h-2 transition-all duration-300 rounded-full right-px top-px bg-light-purple"
            :class="[measurement.comment ? 'opacity-100' : 'opacity-0']"
          ></div>
        </div>
        <div>
          <BlInputButton
            v-if="sessionStatus === 'draft'"
            type="plain"
            icon="close"
            title="Delete"
            dense
            :loading="deleteLoading"
            @clicked="onDelete"
          />
          <BlInputToggle
            v-else
            :checked="!isDropped"
            :disabled="sessionStatus !== 'ongoing'"
            :loading="dropLoading"
            :class="{ 'opacity-50': !isDropped }"
            @changed="onDrop"
          />
        </div>
      </div>

      <div
        v-if="cardLoading"
        class="flex flex-col items-center justify-center h-full bg-pure-white"
      >
        <div class="blum-loader"></div>
      </div>
      <div
        v-else
        id="measurememt-body"
        class="flex flex-col h-full gap-2 pb-3 overflow-y-auto rounded-b"
        :class="{
          'pt-3': isCollapsed
        }"
        :style="{
          background:
            sessionStatus === 'draft' && measurement.target.probing_enable
              ? 'linear-gradient(180deg, #FFF 75%, #F2F8CF 100%)'
              : '#FFFFFF'
        }"
      >
        <div v-if="!isCollapsed" class="flex flex-col gap-1 px-4 py-3">
          <div
            class="flex items-center gap-x-2"
            :class="[
              display === 'target' || display === 'comment' ? '' : 'flex-wrap'
            ]"
          >
            <BlTextSubtitle3
              v-if="measurement.target"
              :text="measurement.target.curriculum_name"
              :title="measurement.target.curriculum_name"
              class="text-slate-700"
              :class="{
                'truncate ': display === 'target' || display === 'comment'
              }"
            />
            <div
              v-if="measurement.type && measurement.type.includes('Probing')"
              class="flex-shrink-0"
            >
              <BlTextBody3
                text="Probing"
                class="px-2 rounded-full text-lime-700 bg-lime-200"
              />
            </div>
          </div>
          <BlTextSubtitle2
            v-if="measurement.target"
            :text="measurement.target.name"
            :title="measurement.target.name"
            class="text-slate-900"
            :class="{
              'truncate ': display === 'target' || display === 'comment'
            }"
          />
        </div>
        <div v-if="display === 'target'" class="flex h-full gap-4 px-4">
          <div
            v-if="isDropped"
            class="flex flex-col items-center justify-center flex-grow flex-shrink-0 gap-4 pb-3"
          >
            <BlIcon
              icon="clipboard-close"
              color="pending-4"
              class="w-20 h-20"
            />
            <div
              v-if="!isCollapsed"
              class="flex flex-col items-center w-full gap-2 max-w-72"
            >
              <BlTextSubtitle2 text="Entry not recorded" class="text-center" />
              <BlTextBody2
                text="This entry will not be saved when the Session ends. Toggle back to save this entry recording."
                class="text-center"
              />
            </div>
          </div>
          <div v-else :key="`measurement-card-${cardId}`" class="w-full h-full">
            <Duration
              v-if="measurementType.includes('Duration')"
              :measurement="measurement"
              :sessionStatus="sessionStatus"
              :isStarted="durationStarted"
              :timer="durationTimerRunning"
              :updateLoading="durationLoading"
              :isCollapsed="isCollapsed"
              @toggle-timer="onToggleDurationTimer"
            />
            <Frequency
              v-if="measurementType.includes('Frequency')"
              :measurement="measurement"
              :sessionStatus="sessionStatus"
              :isCollapsed="isCollapsed"
              @toggle-updated="onToggleUpdated($event)"
            />
            <PartialIntervalRecording
              v-if="measurementType.includes('Pir')"
              :measurement="measurement"
              :sessionStatus="sessionStatus"
              :counter="count"
              :isCollapsed="isCollapsed"
            />
            <Percentage
              v-if="measurementType.includes('Percentage')"
              :measurement="measurement"
              :sessionStatus="sessionStatus"
              :isCollapsed="isCollapsed"
              @toggle-updated="onToggleUpdated($event)"
            />
            <Probing
              v-if="measurementType.includes('Probing')"
              :measurement="measurement"
              :sessionStatus="sessionStatus"
              :isCollapsed="isCollapsed"
              @toggle-collapsed="isCollapsed = $event"
            />
            <Prompting
              v-if="measurementType.includes('Prompting')"
              :measurement="measurement"
              :measurementResults="measurement.results || {}"
              :target="target"
              :sessionStatus="sessionStatus"
              :isCollapsed="isCollapsed"
              @toggle-updated="onToggleUpdated($event)"
            />
            <SkillBasedTreatment
              v-if="measurementType.includes('Sbt')"
              :measurement="measurement"
              :measurementResults="measurement.results || {}"
              :target="target"
              :sessionStatus="sessionStatus"
              :isCollapsed="isCollapsed"
              @toggle-saved="onToggleSaved($event)"
            />
          </div>
          <div v-if="measurement.is_fixed" class="w-8 lg:hidden">
            <div
              class="flex flex-col items-center justify-center flex-shrink-0 w-8 h-full rounded-full bg-slate-400"
              @click="isCollapsed = !isCollapsed"
            >
              <BlIcon
                icon="chevron-up"
                color="slate-8"
                class="w-5 h-5"
                :style="
                  isCollapsed
                    ? { transform: 'rotate(0deg)' }
                    : { transform: 'rotate(180deg)' }
                "
              />
              <BlIcon
                icon="chevron-up"
                color="slate-8"
                class="w-5 h-5 -mt-2"
                :style="
                  isCollapsed
                    ? { transform: 'rotate(0deg)' }
                    : { transform: 'rotate(180deg)' }
                "
              />
            </div>
          </div>
        </div>
        <div
          v-if="display === 'description'"
          class="relative flex flex-col justify-between h-full px-4 overflow-y-auto"
        >
          <div class="flex flex-col gap-3 py-3">
            <div class="flex flex-col gap-0.5 text-wrap">
              <BlTextBody2 :text="targetTypes[measurement.target.type]" />
              <div
                v-if="measurement.target.type === 'Target::Duration'"
                class="flex flex-col gap-0.5"
              >
                <div>
                  <BlTextBody2 text="Goal time:" />
                  <BlTextBody2
                    :text="measurement.target.goal_time.split(':').join(' : ')"
                  />
                </div>
                <div>
                  <BlTextBody2 text="Success metric:" />
                  <BlTextBody2 :text="measurement.target.success_metric" />
                </div>
              </div>
              <div
                v-if="measurement.target.type === 'Target::Percentage'"
                class="flex flex-col gap-0.5"
              >
                <div>
                  <BlTextBody2 text="Goal:" />
                  <BlTextBody2 :text="`${measurement.target.goal}%`" />
                </div>
                <div>
                  <BlTextBody2 text="Number of trials:" />
                  <BlTextBody2 :text="measurement.target.number_of_trial" />
                  <BlTextBody2 text="trial(s)" />
                </div>
                <div>
                  <BlTextBody2 text="Success metric:" />
                  <BlTextBody2 :text="measurement.target.success_metric" />
                </div>
              </div>
              <div
                v-if="measurement.target.type === 'Target::Pir'"
                class="flex flex-col gap-0.5"
              >
                <div>
                  <BlTextBody2 text="Goal:" />
                  <BlTextBody2 :text="`${measurement.target.goal}%`" />
                </div>
                <div>
                  <BlTextBody2 text="Interval:" />
                  <BlTextBody2 :text="measurement.target.interval" />
                  <BlTextBody2 text="minute(s)" />
                </div>
                <div>
                  <BlTextBody2 text="Duration:" />
                  <BlTextBody2 :text="measurement.target.duration" />
                  <BlTextBody2 text="minute(s)" />
                </div>
                <div>
                  <BlTextBody2 text="Success metric:" />
                  <BlTextBody2 :text="measurement.target.success_metric" />
                </div>
              </div>
              <div
                v-if="measurement.target.type === 'Target::Frequency'"
                class="flex flex-col gap-0.5"
              >
                <div>
                  <BlTextBody2 text="Goal:" />
                  <BlTextBody2 :text="measurement.target.goal" />
                  <BlTextBody2 text="attempt(s) per session" />
                </div>
                <div>
                  <BlTextBody2 text="Success metric:" />
                  <BlTextBody2 :text="measurement.target.success_metric" />
                </div>
              </div>
              <div
                v-if="measurement.target.type === 'Target::Prompting'"
                class="flex flex-col gap-0.5"
              >
                <div v-if="measurement.target.prompting_format === 'classic'">
                  <BlTextBody2
                    text="Goal and success metric: Achieve target with"
                  />
                  <BlTextBody2 :text="measurement.target.success_metric" />
                  <BlTextBody2 text="prompt, minimum" />
                  <BlTextBody2 :text="measurement.target.goal" />
                  <BlTextBody2 text="attempt(s) per session" />
                </div>
                <div v-if="measurement.target.prompting_format === 'custom'">
                  <BlTextBody2 text="Goal:" />
                  <BlTextBody2 :text="`${measurement.target.goal}%`" />
                </div>
                <div>
                  <BlTextBody2 text="Prompts used in this session:" />
                  <BlTextBody2
                    :text="
                      Object.keys(measurement.results)
                        .map(key => {
                          const found = target.prompts.find(
                            i => i.id === Number(key)
                          );
                          return {
                            ...measurement.results[key],
                            percentage: found.score || 0
                          };
                        })
                        .sort((a, b) => a.position - b.position)
                        .map(prompt => {
                          if (target.prompting_format === 'custom') {
                            return `${prompt.name} (${prompt.percentage}%)`;
                          }
                          return prompt.name;
                        })
                        .join(', ')
                    "
                  />
                </div>
              </div>
            </div>
            <div
              v-if="measurementType.includes('Probing')"
              class="flex flex-col gap-0.5 text-wrap"
            >
              <BlTextBody2 text="Probing activated" />
              <div>
                <BlTextBody2 text="Goal: score" />
                <BlTextBody2
                  :text="
                    measurement.target.success_metric ===
                    'equal to or greater than goal'
                      ? '≥'
                      : '<'
                  "
                />
                <BlTextBody2 :text="`${measurement.target.probing_goal}%`" />
                <BlTextBody2 text="in minimum" />
                <BlTextBody2
                  :text="measurement.target.probing_number_of_trial"
                />
                <BlTextBody2 text="trial(s)" />
              </div>
            </div>
            <div class="flex flex-col gap-0.5 text-wrap">
              <BlTextBody2
                v-if="!measurement.target.description"
                text="No description"
                class="italic"
              />
              <BlTextBody2
                v-else
                :text="measurement.target.description"
                class="whitespace-pre-line"
              />
            </div>
            <div v-if="measurement.target.last_phase_line" class="text-wrap">
              <BlTextBody2 text="Data from this session will be added to the" />
              <BlTextBody2
                :text="measurement.target.last_phase_line.label"
                class="font-semibold"
              />
              <BlTextBody2 text="phase." />
            </div>
            <div
              v-if="measurement.target.type === 'Target::Sbt'"
              class="flex flex-col"
            >
              <!-- sbt taks -->
              <div class="flex flex-col gap-3 py-3 border-t-2 border-slate-400">
                <div
                  v-for="taskCode in target.target_tasks"
                  :key="taskCode.id"
                  class="flex flex-col gap-1"
                >
                  <div>
                    <BlTextSubtitle3 :text="taskCode.code" />
                    <BlTextSubtitle3 text="-" />
                    <BlTextSubtitle3 :text="taskCode.title" />
                  </div>
                  <BlTextBody2
                    :text="taskCode.description"
                    class="whitespace-pre-line"
                  />
                </div>
              </div>
              <!-- sbt problem behavior -->
              <div class="flex flex-col gap-3 py-3 border-t-2 border-slate-400">
                <div
                  v-for="problemBehavior in target.target_problem_behaviors"
                  :key="problemBehavior.id"
                  class="flex flex-col gap-1"
                >
                  <div>
                    <BlTextSubtitle3 :text="problemBehavior.code" />
                    <BlTextSubtitle3 text="-" />
                    <BlTextSubtitle3 :text="problemBehavior.code_definition" />
                  </div>
                  <BlTextBody2
                    :text="problemBehavior.description"
                    class="whitespace-pre-line"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="sticky bottom-0 w-full py-3 bg-pure-white">
            <BlInputButton
              type="outlined"
              dense
              label="Close"
              class="w-full"
              @clicked="display = 'target'"
            />
          </div>
        </div>
        <div
          v-if="display === 'comment'"
          class="flex flex-col justify-between h-full gap-3 px-4"
        >
          <BlInputText
            v-if="sessionStatus === 'ongoing'"
            :input="commentInput"
            class="h-full mt-2"
            inputClass="h-full min-h-64"
            @changed="inputHandler"
          />
          <BlInputText
            v-else-if="sessionStatus === 'draft'"
            :input="{
              placeholder: 'Type your comment here... ',
              textarea: true
            }"
            class="h-full mt-2"
            inputClass="h-full min-h-64"
          />
          <BlTextBody2
            v-else
            :text="measurement.comment || '-'"
            class="pt-3 text-wrap"
          />
          <div class="sticky bottom-0 w-full py-3 bg-pure-white">
            <div v-if="sessionStatus === 'ongoing'" class="grid grid-cols-2">
              <BlInputButton
                type="plain"
                dense
                label="Cancel"
                class="w-full"
                @clicked="display = 'target'"
              />
              <BlInputButton
                dense
                label="Save"
                class="w-full"
                :disabled="isDisabledSaveComment"
                :loading="commentLoading"
                @clicked="onSaveComment"
              />
            </div>
            <BlInputButton
              v-else
              type="outlined"
              dense
              label="Close"
              class="w-full"
              @clicked="display = 'target'"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import Duration from "./card/Duration.vue";
import Frequency from "./card/Frequency.vue";
import PartialIntervalRecording from "./card/PartialIntervalRecording.vue";
import Percentage from "./card/Percentage.vue";
import Probing from "./card/Probing.vue";
import Prompting from "./card/Prompting.vue";
import SkillBasedTreatment from "./card/SkillBasedTreatment.vue";
import StatusChip from "../clients/target/StatusChip.vue";

import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputToggle from "../BlInputToggle.vue";
import BlInputText from "../BlInputText.vue";

export default {
  name: "MeasurementCard",
  components: {
    Duration,
    Frequency,
    PartialIntervalRecording,
    Percentage,
    Probing,
    Prompting,
    SkillBasedTreatment,
    StatusChip,
    //
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlInputText
  },
  props: {
    measurement: { type: Object, required: true },
    sessionStatus: { type: String, required: true },
    isRunning: { type: Boolean, default: false },
    count: { type: Number, required: false },
    readonly: { type: Boolean, default: false }
    // @toggle-updated
    // @toggle-running
    // @toggle-saved
    // @exit-session
  },
  data() {
    return {
      cardLoading: true,
      display: "target", // target | description | comment
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting"
      },
      isCollapsed: false,
      //
      dropLoading: false,
      isDropped: this.measurement.is_dropped || false,
      cardId: "random-id",
      //
      commentLoading: false,
      commentInput: {
        placeholder: "Type your comment here...",
        model: this.measurement.comment || "",
        textarea: true
      },
      //
      deleteLoading: false,
      // duration property
      durationCounter: 0,
      durationStarted: false,
      durationLoading: false,
      durationInterval: null,
      // prompting & sbt property
      target: {}
    };
  },
  computed: {
    ...mapGetters(["viewMode", "measurements"]),
    measurementType() {
      const target = this.measurement.target || this.target || {};
      return this.measurement.type || target.type || "";
    },
    isDisabledSaveComment() {
      return this.commentInput.model === (this.measurement.comment || "");
    },
    // duration property
    durationTimerRunning() {
      let hours = "00";
      let minutes = "00";
      let seconds = "00";
      if (this.durationCounter) {
        seconds = (this.durationCounter % 60).toString().padStart(2, "0");
        if (this.durationCounter >= 60) {
          minutes = Math.floor(this.durationCounter / 60);
          if (this.durationCounter >= 3600) minutes = minutes % 60;
          minutes = minutes.toString().padStart(2, "0");
        }
        if (this.durationCounter >= 3600) {
          hours = Math.floor(this.durationCounter / 3600)
            .toString()
            .padStart(2, "0");
        }
      }
      return `${hours}:${minutes}:${seconds}`;
    }
  },
  watch: {
    viewMode(val) {
      this.setupView(val);
    },
    measurement: {
      deep: true,
      handler(val) {
        this.isDropped = val.is_dropped;
      }
    },
    isDropped() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      this.cardId = `card-random-id-${combination}`;
    },
    isCollapsed(val) {
      if (val) this.display = "target";
    },
    display(val) {
      if (val === "comment") {
        this.commentInput.model = this.measurement.comment || "";
      }
    }
  },
  async mounted() {
    if (this.measurement.type === "Measurement::Duration") {
      if (this.measurement.results && this.measurement.results.seconds) {
        this.durationCounter = this.measurement.results.seconds;
      }
    }

    if (
      this.measurement.type === "Measurement::Probing" ||
      this.measurement.type === "Measurement::Prompting" ||
      this.measurement.type === "Measurement::Sbt"
    ) {
      const { data } = await this.getTarget({ id: this.measurement.target_id });
      this.target = data || {};
    }

    this.isDropped = this.measurement.is_dropped || false;
    this.cardLoading = false;
    this.setupView(this.viewMode);
  },
  methods: {
    ...mapActions([
      "getTarget",
      "updateMeasurement",
      "updateMeasurementResults",
      "updateMeasurementIsDropped",
      "deleteMeasurement",
      "showToast"
    ]),
    setupView(val) {
      if (val !== "desktop" && this.measurement.is_fixed) {
        this.isCollapsed = true;
      } else {
        this.isCollapsed = false;
      }
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    //
    async onSaveComment() {
      const params = {
        id: this.measurement.id,
        measurement: {
          comment: this.commentInput.model
        }
      };

      this.commentLoading = true;
      const { success } = await this.updateMeasurement(params);
      this.commentLoading = false;
      if (!success) return;
      const title = `Comment saved`;
      const message = `Success! The comment has been saved.`;
      this.showToast({ title, message, type: "success" });
      this.display = "target";
    },
    //
    async onDrop() {
      if (this.sessionStatus !== "ongoing") return;
      const params = {
        id: this.measurement.id,
        measurement: { is_dropped: !this.measurement.is_dropped }
      };
      this.dropLoading = true;
      const { success, data } = await this.updateMeasurementIsDropped(params);
      this.dropLoading = false;
      if (!success) return;
      this.isDropped = data.is_dropped;
      if (data.is_dropped && this.isRunning) {
        this.$emit("toggle-running");
      }
    },
    async onToggleUpdated(updated) {
      this.$emit("toggle-updated", { id: this.measurement.id, updated });
    },
    //
    async onDelete() {
      if (this.deleteLoading) return;
      if (this.sessionStatus !== "draft") return;

      const params = {
        id: this.measurement.id,
        params: ""
      };
      if (this.measurement.type.includes("Probing")) {
        params.params = "?delete_card_probing=true";
      }

      this.deleteLoading = true;
      await this.deleteMeasurement(params);
      this.deleteLoading = false;
    },
    // duration property
    onStartDurationTimer() {
      this.durationInterval = setInterval(() => {
        this.durationCounter++;
      }, 1000);
    },
    async onToggleDurationTimer() {
      if (this.sessionStatus !== "ongoing") return;
      if (this.durationLoading) return;
      if (!this.durationStarted) {
        this.durationStarted = true;
        this.onStartDurationTimer();
        this.$emit("toggle-running");
      } else {
        clearInterval(this.durationInterval);
        const params = {
          id: this.measurement.id,
          results: this.durationTimerRunning
        };
        this.durationLoading = true;
        const { success } = await this.updateMeasurementResults(params);
        this.durationLoading = false;
        if (!success) {
          this.onStartDurationTimer();
          return;
        }
        this.durationStarted = false;
        this.$emit("toggle-running");
      }
    },
    // sbt property
    async onToggleSaved(saved) {
      this.$emit("toggle-saved", { id: this.measurement.id, saved });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/MeasurementCard.vue